import React, { useId, useRef, useState } from 'react';
import NiceModal, { antdModalV5, useModal } from '@ebay/nice-modal-react';
import styled from 'styled-components';

import { OrbitMutationOutcome } from 'api/orbit';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import OrbitThemeScope from '../../OrbitThemeScope';
import { TOrbit } from '../../i18n/TModules';
import { OrbitSession } from '../../types';
import {
  Popup,
  POPUP_SCALE,
  PopupCancel,
  PopupConfirm,
  PopupError,
  PopupLine
} from './OrbitChatPopup';

/** The server's own limit; it trims leading and trailing whitespace before counting. */
export const ORBIT_TITLE_MAX = 120;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
`;

const Field = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #212d4b;
  border-radius: 9px;
  outline: none;
  background: #101830;
  color: #f5f7fb;
  font: inherit;

  &:focus-visible {
    border-color: #268aff;
  }
`;

const Hint = styled(PopupLine)`
  margin-top: 6px;
  color: #8da0bd;
`;

export interface IOrbitRenameChatModalProps {
  /** The title the chat shows now. */
  title: string;
  /** Sends the PATCH: a trimmed title, or `null` to name the chat by its first question again. */
  onConfirm: (title: string | null) => Promise<OrbitMutationOutcome<OrbitSession>>;
  /** Any failure but a refused title: the dialog stays open so the typing is not lost. */
  onFailure: (outcome: Exclude<OrbitMutationOutcome<OrbitSession>, { kind: 'ok' }>) => void;
}

/** "Rename chat": an emptied name restores the default title; the server's answer is what shows. */
const OrbitRenameChatModal = NiceModal.create(
  ({ title, onConfirm, onFailure }: IOrbitRenameChatModalProps) => {
    const modal = useModal();
    const modalProps = antdModalV5(modal);
    const { t } = useMultiLanguageTranslation();
    const fieldId = useId();
    const hintId = useId();
    const [value, setValue] = useState(title);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inFlight = useRef(false);

    const close = () => {
      modal.resolve();
      modal.hide();
    };

    const handleSave = async (event?: React.FormEvent) => {
      event?.preventDefault();
      if (inFlight.current) {
        return;
      }

      const trimmed = value.trim();
      if (trimmed === title) {
        close();
        return;
      }

      inFlight.current = true;
      setSubmitting(true);
      setError(null);

      const outcome = await onConfirm(trimmed === '' ? null : trimmed);

      inFlight.current = false;
      setSubmitting(false);

      if (outcome.kind === 'ok' || outcome.kind === 'notFound') {
        close();
      }
      if (outcome.kind === 'invalid') {
        setError(outcome.message ?? t(`${TOrbit}.page.renameChat.invalid`));
      } else if (outcome.kind !== 'ok') {
        onFailure(outcome);
      }
    };

    const handleCancel = () => {
      if (!inFlight.current) {
        close();
      }
    };

    return (
      <Popup
        {...modalProps}
        // Portalled to `document.body`, so it re-declares the tokens the content reads.
        modalRender={node => <OrbitThemeScope>{node}</OrbitThemeScope>}
        title={t(`${TOrbit}.page.renameChat.title`)}
        width={POPUP_SCALE.width}
        centered
        closable={false}
        maskClosable={!submitting}
        keyboard={!submitting}
        onCancel={handleCancel}
        footer={[
          <PopupCancel key="cancel" disabled={submitting} onClick={handleCancel}>
            {t(`${TOrbit}.actions.cancel`)}
          </PopupCancel>,
          <PopupConfirm
            key="save"
            type="primary"
            loading={submitting}
            aria-busy={submitting}
            onClick={() => handleSave()}
          >
            {t(`${TOrbit}.page.renameChat.save`)}
          </PopupConfirm>
        ]}
        data-testid="orbit-rename-chat"
      >
        <form onSubmit={handleSave}>
          <Label htmlFor={fieldId}>{t(`${TOrbit}.page.renameChat.label`)}</Label>
          <Field
            id={fieldId}
            value={value}
            maxLength={ORBIT_TITLE_MAX}
            aria-describedby={hintId}
            // The dialog opens to type a name; focus going straight to the field is the point.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onChange={event => setValue(event.target.value)}
          />
          <Hint id={hintId}>{t(`${TOrbit}.page.renameChat.hint`)}</Hint>
        </form>
        {error !== null ? (
          <PopupError role="alert" data-testid="orbit-rename-chat-error">
            {error}
          </PopupError>
        ) : null}
      </Popup>
    );
  }
);

export default OrbitRenameChatModal;
